import { Component, Input, Output, EventEmitter, TemplateRef } from '@angular/core';

export type DataTableAlignment = 'left' | 'center' | 'right';
export type DataTableCellHighlight = 'orange' | 'green' | 'default';
export type DataTableVariant = 'standard' | 'compact' | 'striped';
export type DataTableSortDirection = 'asc' | 'desc' | '';

export interface DataTableColumn {
  field: string;
  header: string;
  align?: DataTableAlignment;
  tooltip?: string;
  prefix?: string;
  suffix?: string;
  numberFormat?: boolean;
  highlight?: DataTableCellHighlight;
  isBold?: boolean;
  sortable?: boolean;
  cellTemplate?: TemplateRef<any>;
  onCellClick?: (row: any) => void;
}

@Component({
  selector: 'app-data-table',
  templateUrl: './data-table.component.html',
  styleUrls: ['./data-table.component.scss']
})
export class DataTableComponent {
  @Input() columns: DataTableColumn[] = [];
  @Input() rows: any[] = [];
  @Input() isLoading = false;
  @Input() itemsPerPage = 5;
  @Input() emptyMessage = 'No data available';
  @Input() variant: DataTableVariant = 'standard';
  @Input() totalItems: number = 0;
  @Input() page: number = 1;
  @Input() sortBy: string = '';
  @Input() sortDir: DataTableSortDirection = '';
  @Input() expandedRowTemplate?: TemplateRef<any>;
  @Input() isRowExpanded?: (row: any) => boolean;
  @Input() rowClass?: (row: any) => string;
  @Output() pageChange = new EventEmitter<number>();
  @Output() sortChange = new EventEmitter<{ sortBy: string; sortDir: DataTableSortDirection }>();
  @Output() cellClick = new EventEmitter<{ field: string; row: any }>();

  get totalPages(): number {
    return Math.ceil(this.totalItems / this.itemsPerPage);
  }

  get hasRows(): boolean {
    return this.totalItems > 0;
  }

  get displayRangeStart(): number {
    return (this.page - 1) * this.itemsPerPage + 1;
  }

  get displayRangeEnd(): number {
    return Math.min(this.page * this.itemsPerPage, this.totalItems);
  }

  get tableClasses(): Record<string, boolean> {
    return {
      'dt-table--compact': this.variant === 'compact',
      'dt-table--striped': this.variant === 'striped'
    };
  }

  getHeaderClasses(col: DataTableColumn): Record<string, boolean> {
    return {
      [`dt-header-cell--${col.align || 'left'}`]: true,
      'dt-header-cell--sortable': col.sortable === true,
      'dt-header-cell--sorted': col.sortable === true && col.field === this.sortBy && this.sortDir !== ''
    };
  }

  getCellClasses(col: DataTableColumn): Record<string, boolean> {
    return {
      [`dt-cell--${col.align || 'left'}`]: true,
      [`dt-cell--${col.highlight}`]: !!(col.highlight && col.highlight !== 'default'),
      'dt-cell--bold': col.isBold === true
    };
  }

  getSortIcon(col: DataTableColumn): string {
    if (col.field === this.sortBy) {
      if (this.sortDir === 'asc') return 'arrow_upward';
      if (this.sortDir === 'desc') return 'arrow_downward';
    }
    return 'unfold_more';
  }

  onSort(col: DataTableColumn): void {
    if (!col.sortable) return;
    let direction: DataTableSortDirection;
    if (this.sortBy !== col.field) {
      direction = 'asc';
    } else if (this.sortDir === 'asc') {
      direction = 'desc';
    } else if (this.sortDir === 'desc') {
      direction = '';
    } else {
      direction = 'asc';
    }
    this.sortChange.emit({ sortBy: col.field, sortDir: direction });
  }

  onCellClick(col: DataTableColumn, row: any): void {
    this.cellClick.emit({ field: col.field, row });
    if (col.onCellClick) {
      col.onCellClick(row);
    }
  }

  formatNumber(value: any): string {
    const parsed = parseFloat(value);
    if (isNaN(parsed)) { return value != null ? String(value) : ''; }
    return parsed.toLocaleString('en-US');
  }

  previousPage(): void {
    if (this.page > 1) {
      this.pageChange.emit(this.page - 1);
    }
  }

  nextPage(): void {
    if (this.page < this.totalPages) {
      this.pageChange.emit(this.page + 1);
    }
  }
}
